import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
type Vendor = { id: string; vendor_name: string };
type Dept = { id: string; department_name: string };
type Bill = {
  id: string;
  vendor_id: string;
  department_id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  entry_type: string;
  category: string;
  description: string;
  amount_yen: number;
  payable_vendors: { vendor_name: string } | null;
  staff_departments: { department_name: string } | null;
  payable_payments: { amount_yen: number }[];
};
const yen = new Intl.NumberFormat("ja-JP", {
  style: "currency",
  currency: "JPY",
  maximumFractionDigits: 0,
});
const today = () =>
  new Intl.DateTimeFormat("sv-SE", { timeZone: "Asia/Tokyo" }).format(
    new Date(),
  );
export default function PayablesPage() {
  const [bills, setBills] = useState<Bill[]>([]),
    [vendors, setVendors] = useState<Vendor[]>([]),
    [departments, setDepartments] = useState<Dept[]>([]),
    [loading, setLoading] = useState(true),
    [saving, setSaving] = useState(false),
    [message, setMessage] = useState<string | null>(null),
    [vendorOpen, setVendorOpen] = useState(false),
    [billOpen, setBillOpen] = useState(false),
    [paymentBill, setPaymentBill] = useState<Bill | null>(null),
    [invoice, setInvoice] = useState<File | null>(null);
  const [vendor, setVendor] = useState({
      name: "", contact: "", terms: "30", paymentType: "invoice",
      method: "bank_transfer", closingDay: "", monthOffset: "1", paymentDay: "", termsNote: "",
    }),
    [bill, setBill] = useState({
      vendorId: "",
      departmentId: "",
      number: "",
      invoiceDate: today(),
      dueDate: today(),
      type: "operating_expense",
      category: "",
      description: "",
      amount: "",
      notes: "",
    }),
    [payment, setPayment] = useState({
      date: today(),
      amount: "",
      method: "bank_transfer",
      reference: "",
      notes: "",
    });
  const load = useCallback(async () => {
    setLoading(true);
    const [b, v, d] = await Promise.all([
      supabase
        .from("payable_bills")
        .select(
          "id,vendor_id,department_id,invoice_number,invoice_date,due_date,entry_type,category,description,amount_yen,payable_vendors(vendor_name),staff_departments(department_name),payable_payments(amount_yen)",
        )
        .order("due_date", { ascending: false }),
      supabase
        .from("payable_vendors")
        .select("id,vendor_name")
        .eq("is_active", true)
        .order("vendor_name"),
      supabase
        .from("staff_departments")
        .select("id,department_name")
        .eq("is_active", true)
        .order("sort_order"),
    ]);
    if (b.error || v.error || d.error)
      setMessage("買掛金を読み込めませんでした。");
    else {
      setBills((b.data || []) as unknown as Bill[]);
      setVendors((v.data || []) as Vendor[]);
      setDepartments((d.data || []) as Dept[]);
    }
    setLoading(false);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const paid = (b: Bill) =>
    b.payable_payments.reduce((a, p) => a + p.amount_yen, 0);
  const total = useMemo(
    () => bills.reduce((a, b) => a + b.amount_yen - paid(b), 0),
    [bills],
  );
  const overdue = useMemo(
    () =>
      bills.reduce(
        (a, b) => a + (b.due_date < today() ? b.amount_yen - paid(b) : 0),
        0,
      ),
    [bills],
  );
  const saveVendor = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const { error } = await supabase.rpc("admin_save_payable_vendor", {
      p_name: vendor.name,
      p_contact: vendor.contact || null,
      p_terms: Number(vendor.terms),
      p_payment_type: vendor.paymentType,
      p_default_method: vendor.method,
      p_closing_day: vendor.closingDay ? Number(vendor.closingDay) : null,
      p_month_offset: Number(vendor.monthOffset),
      p_payment_day: vendor.paymentDay ? Number(vendor.paymentDay) : null,
      p_terms_note: vendor.termsNote || null,
    });
    setSaving(false);
    if (error) setMessage("支払先を保存できませんでした。");
    else {
      setVendorOpen(false);
      await load();
    }
  };
  const saveBill = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoice) {
      setMessage("請求書画像またはPDFを選んでください。");
      return;
    }
    setSaving(true);
    const path = `${bill.invoiceDate}/${crypto.randomUUID()}.${(invoice.name.split(".").pop() || "pdf").toLowerCase()}`;
    const up = await supabase.storage
      .from("payable-invoices")
      .upload(path, invoice, { contentType: invoice.type });
    if (up.error) {
      setSaving(false);
      setMessage("請求書をアップロードできませんでした。");
      return;
    }
    const { error } = await supabase.rpc("admin_create_payable_bill", {
      p_vendor_id: bill.vendorId,
      p_department_id: bill.departmentId,
      p_purchase_request_id: null,
      p_invoice_number: bill.number,
      p_invoice_date: bill.invoiceDate,
      p_due_date: bill.dueDate,
      p_entry_type: bill.type,
      p_category: bill.category,
      p_description: bill.description,
      p_amount: Number(bill.amount),
      p_invoice_path: path,
      p_notes: bill.notes || null,
    });
    setSaving(false);
    if (error)
      setMessage("請求書を登録できませんでした。番号重複も確認してください。");
    else {
      setBillOpen(false);
      setInvoice(null);
      await load();
    }
  };
  const savePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentBill) return;
    setSaving(true);
    const { error } = await supabase.rpc("admin_record_payable_payment", {
      p_bill_id: paymentBill.id,
      p_payment_date: payment.date,
      p_amount: Number(payment.amount),
      p_method: payment.method,
      p_reference: payment.reference || null,
      p_notes: payment.notes || null,
    });
    setSaving(false);
    if (error)
      setMessage(
        "支払いを記録できませんでした。未払額を超えていないか確認してください。",
      );
    else {
      setPaymentBill(null);
      await load();
    }
  };
  return (
    <div className="p-4 md:p-7 max-w-6xl mx-auto space-y-5">
      <header className="flex justify-between items-end gap-3">
        <div>
          <p className="text-xs font-bold tracking-[.16em] uppercase text-primary-700">
            Accounts Payable
          </p>
          <h1 className="text-2xl font-extrabold mt-1">買掛・支払管理</h1>
          <p className="text-sm text-foreground-600 mt-1">
            請求書受領で費用計上し、支払いは未払残高へ反映します。
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setVendorOpen(true)}
            className="border rounded-lg px-3 py-2 font-bold"
          >
            支払先
          </button>
          <button
            onClick={() => {
              setBill({
                ...bill,
                vendorId: vendors[0]?.id || "",
                departmentId: departments[0]?.id || "",
              });
              setBillOpen(true);
            }}
            className="bg-primary-800 text-white rounded-lg px-3 py-2 font-bold"
          >
            ＋請求書
          </button>
        </div>
      </header>
      {message && (
        <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm">
          {message}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Card label="未払残高" value={yen.format(total)} />
        <Card
          label="期限超過"
          value={yen.format(overdue)}
          danger={overdue > 0}
        />
      </div>
      <section className="rounded-xl border bg-white divide-y">
        {loading ? (
          <p className="p-10 text-center">読み込み中…</p>
        ) : bills.length === 0 ? (
          <p className="p-10 text-center text-foreground-500">
            請求書はありません。
          </p>
        ) : (
          bills.map((b) => {
            const rest = b.amount_yen - paid(b);
            return (
              <div
                key={b.id}
                className="p-4 grid md:grid-cols-[1fr_160px_140px] gap-3 items-center"
              >
                <div>
                  <b>
                    {b.payable_vendors?.vendor_name} · {b.invoice_number}
                  </b>
                  <p className="text-sm mt-1">{b.description}</p>
                  <p className="text-xs text-foreground-500">
                    {b.staff_departments?.department_name} · 期限 {b.due_date}
                  </p>
                </div>
                <div className="text-right">
                  <b>{yen.format(b.amount_yen)}</b>
                  <p className="text-xs">未払 {yen.format(rest)}</p>
                </div>
                {rest > 0 ? (
                  <button
                    onClick={() => {
                      setPaymentBill(b);
                      setPayment({
                        ...payment,
                        date: today(),
                        amount: String(rest),
                      });
                    }}
                    className="bg-primary-50 text-primary-800 rounded-lg px-3 py-2 font-bold"
                  >
                    支払記録
                  </button>
                ) : (
                  <span className="text-center text-primary-700 font-bold">
                    支払済み
                  </span>
                )}
              </div>
            );
          })
        )}
      </section>
      {vendorOpen && (
        <Modal title="支払先を登録" close={() => setVendorOpen(false)}>
          <form onSubmit={saveVendor} className="space-y-3">
            <Field label="支払先名">
              <input
                required
                value={vendor.name}
                onChange={(e) => setVendor({ ...vendor, name: e.target.value })}
              />
            </Field>
            <Field label="連絡先">
              <input
                value={vendor.contact}
                onChange={(e) =>
                  setVendor({ ...vendor, contact: e.target.value })
                }
              />
            </Field>
            <Field label="支払サイト（日）">
              <input
                type="number"
                min="0"
                value={vendor.terms}
                onChange={(e) =>
                  setVendor({ ...vendor, terms: e.target.value })
                }
              />
            </Field>
            <Field label="支払い条件">
              <select value={vendor.paymentType} onChange={(e)=>setVendor({...vendor,paymentType:e.target.value})}>
                <option value="invoice">請求書払い</option><option value="cash_on_delivery">納品時現金</option><option value="prepaid">前払い</option><option value="pay_as_you_go">都度払い</option>
              </select>
            </Field>
            <Field label="標準の支払方法">
              <select value={vendor.method} onChange={(e)=>setVendor({...vendor,method:e.target.value})}>
                <option value="bank_transfer">銀行振込</option><option value="cash">現金</option><option value="card">カード</option><option value="direct_debit">口座振替</option><option value="other">その他</option>
              </select>
            </Field>
            <div className="grid grid-cols-3 gap-2">
              <Field label="締日"><input type="number" min="1" max="31" value={vendor.closingDay} onChange={(e)=>setVendor({...vendor,closingDay:e.target.value})}/></Field>
              <Field label="何か月後"><input type="number" min="0" max="3" value={vendor.monthOffset} onChange={(e)=>setVendor({...vendor,monthOffset:e.target.value})}/></Field>
              <Field label="支払日"><input type="number" min="1" max="31" value={vendor.paymentDay} onChange={(e)=>setVendor({...vendor,paymentDay:e.target.value})}/></Field>
            </div>
            <Field label="支払条件メモ"><textarea rows={2} value={vendor.termsNote} onChange={(e)=>setVendor({...vendor,termsNote:e.target.value})}/></Field>
            <Save saving={saving} />
          </form>
        </Modal>
      )}
      {billOpen && (
        <Modal title="請求書を登録" close={() => setBillOpen(false)}>
          <form onSubmit={saveBill} className="space-y-3">
            <Field label="支払先">
              <select
                required
                value={bill.vendorId}
                onChange={(e) => setBill({ ...bill, vendorId: e.target.value })}
              >
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.vendor_name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="事業・部署">
              <select
                required
                value={bill.departmentId}
                onChange={(e) =>
                  setBill({ ...bill, departmentId: e.target.value })
                }
              >
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.department_name}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="請求書番号">
                <input
                  required
                  value={bill.number}
                  onChange={(e) => setBill({ ...bill, number: e.target.value })}
                />
              </Field>
              <Field label="金額">
                <input
                  required
                  type="number"
                  min="1"
                  value={bill.amount}
                  onChange={(e) => setBill({ ...bill, amount: e.target.value })}
                />
              </Field>
              <Field label="請求日">
                <input
                  required
                  type="date"
                  value={bill.invoiceDate}
                  onChange={(e) =>
                    setBill({ ...bill, invoiceDate: e.target.value })
                  }
                />
              </Field>
              <Field label="支払期限">
                <input
                  required
                  type="date"
                  value={bill.dueDate}
                  onChange={(e) =>
                    setBill({ ...bill, dueDate: e.target.value })
                  }
                />
              </Field>
            </div>
            <Field label="費用区分">
              <select
                value={bill.type}
                onChange={(e) => setBill({ ...bill, type: e.target.value })}
              >
                <option value="operating_expense">経費</option>
                <option value="cost_of_sales">仕入</option>
              </select>
            </Field>
            <Field label="分類">
              <input
                required
                value={bill.category}
                onChange={(e) => setBill({ ...bill, category: e.target.value })}
              />
            </Field>
            <Field label="内容">
              <input
                required
                value={bill.description}
                onChange={(e) =>
                  setBill({ ...bill, description: e.target.value })
                }
              />
            </Field>
            <Field label="請求書画像・PDF">
              <input
                required
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setInvoice(e.target.files?.[0] || null)}
              />
            </Field>
            <Save saving={saving} />
          </form>
        </Modal>
      )}
      {paymentBill && (
        <Modal title="支払いを記録" close={() => setPaymentBill(null)}>
          <form onSubmit={savePayment} className="space-y-3">
            <Field label="支払日">
              <input
                type="date"
                required
                value={payment.date}
                onChange={(e) =>
                  setPayment({ ...payment, date: e.target.value })
                }
              />
            </Field>
            <Field label="支払額">
              <input
                type="number"
                min="1"
                required
                value={payment.amount}
                onChange={(e) =>
                  setPayment({ ...payment, amount: e.target.value })
                }
              />
            </Field>
            <Field label="支払方法">
              <select
                value={payment.method}
                onChange={(e) =>
                  setPayment({ ...payment, method: e.target.value })
                }
              >
                <option value="bank_transfer">銀行振込</option>
                <option value="cash">現金</option>
                <option value="card">カード</option>
                <option value="other">その他</option>
              </select>
            </Field>
            <Field label="振込名義・参照番号">
              <input
                value={payment.reference}
                onChange={(e) =>
                  setPayment({ ...payment, reference: e.target.value })
                }
              />
            </Field>
            <Save saving={saving} />
          </form>
        </Modal>
      )}
    </div>
  );
}
function Card({
  label,
  value,
  danger = false,
}: {
  label: string;
  value: string;
  danger?: boolean;
}) {
  return (
    <div className="rounded-xl border bg-white p-4">
      <p className="text-xs text-foreground-500">{label}</p>
      <p
        className={`text-xl font-extrabold mt-1 ${danger ? "text-red-700" : ""}`}
      >
        {value}
      </p>
    </div>
  );
}
function Modal({
  title,
  close,
  children,
}: {
  title: string;
  close: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-2xl p-5">
        <div className="flex justify-between mb-4">
          <h2 className="font-bold text-lg">{title}</h2>
          <button onClick={close}>
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="text-sm block">
      {label}
      <div className="mt-1 [&>*]:w-full [&>*]:border [&>*]:rounded-lg [&>*]:px-3 [&>*]:py-2">
        {children}
      </div>
    </label>
  );
}
function Save({ saving }: { saving: boolean }) {
  return (
    <button
      disabled={saving}
      className="w-full py-2.5 rounded-lg bg-primary-800 text-white font-bold"
    >
      {saving ? "保存中…" : "保存"}
    </button>
  );
}
